import React, { useState } from "react";
import { useLang } from "@/i18n";
import { OrnateHeartDivider } from "./FloralOrnaments";
import { ChevronDown } from "lucide-react";

function firstCharacter(name) {
    const trimmed = String(name ?? "").trim();
    return trimmed ? Array.from(trimmed)[0] : "";
}

function coverImageSrc(wedding) {
    const configured = typeof wedding?.cover_image_url === "string" ? wedding.cover_image_url.trim() : "";
    return configured || "/assets/wed.jpg";
}

export default function CoverScreen({ wedding, guestName, englishGuestName, onOpen }) {
    const { t, lang } = useLang();
    const englishGreetingName = (englishGuestName || guestName || "").trim();
    const groomName = lang === "ar" ? wedding.groom_ar : wedding.groom_en;
    const brideName = lang === "ar" ? wedding.bride_ar : wedding.bride_en;
    const coupleNames = `${groomName} & ${brideName}`;
    const coverSrc = coverImageSrc(wedding);
    const [failedSrc, setFailedSrc] = useState("");
    const [loadedSrc, setLoadedSrc] = useState("");
    const imageFailed = failedSrc === coverSrc;
    const imageLoaded = loadedSrc === coverSrc;

    return (
        <section
            data-testid="cover-screen"
            className="min-h-[100dvh] wd-paper-texture flex flex-col items-center justify-center px-4 py-6 relative overflow-hidden"
        >
            {/* Gold double frame */}
            <div className="relative w-full max-w-md mx-auto">
                <div className="wd-gold-frame bg-[var(--wd-bg)] rounded-lg overflow-hidden">
                    <div className="relative z-10 px-6 pt-14 pb-8 text-center">
                        <p
                            className="wd-script text-[var(--wd-primary)] text-5xl md:text-6xl leading-none wd-fade-up"
                            data-testid="save-the-date-label"
                            translate="no"
                        >
                            {lang === "ar"
                                ? `أهلًا ${guestName || ""}`.trim()
                                : `Hello ${englishGreetingName}`.trim()}
                        </p>
                        <div className="mt-3 flex justify-center wd-fade-up wd-delay-1">
                            <OrnateHeartDivider width={200} />
                        </div>

                        <div className="mt-4 wd-fade-up wd-delay-1 text-[var(--wd-primary)]">
                            <p className="mt-1 wd-heading text-xl font-medium">
                                {lang === "ar" ? "أنت مدعو لحضور حفل زفاف" : t("invitedToWedding")}
                            </p>
                        </div>

                        {/* Couple image, or a monogram when that image is missing */}
                        <div className="relative mx-auto mt-2 aspect-[4/3] w-full max-w-[340px] wd-fade-up wd-delay-2">
                            <img
                                src={coverSrc}
                                alt={coupleNames}
                                className={`absolute inset-0 h-full w-full rounded-md object-cover object-center ${imageLoaded && !imageFailed ? "" : "hidden"}`}
                                data-testid="cover-couple-image"
                                loading="eager"
                                ref={(node) => {
                                    if (!node || !node.complete || node.getAttribute("src") !== coverSrc) return;
                                    if (node.naturalWidth > 0) setLoadedSrc(coverSrc);
                                    else setFailedSrc(coverSrc);
                                }}
                                onLoad={(event) => {
                                    if (event.currentTarget.naturalWidth > 0) setLoadedSrc(coverSrc);
                                }}
                                onError={() => setFailedSrc(coverSrc)}
                            />
                            {imageFailed && (
                                <div
                                    className="absolute inset-0 flex items-center justify-center rounded-md border-2 border-[var(--wd-accent)] bg-[var(--wd-bg-alt)]"
                                    data-testid="cover-monogram"
                                >
                                    <span
                                        className="wd-script flex items-center gap-3 text-7xl leading-none text-[var(--wd-accent)]"
                                        translate="no"
                                    >
                                        <span>{firstCharacter(groomName)}</span>
                                        <span>{firstCharacter(brideName)}</span>
                                    </span>
                                </div>
                            )}
                        </div>

                        <h1
                            className="wd-script wd-delay-3 wd-fade-up text-[2.35rem] md:text-6xl text-[var(--wd-accent)] mt-4 leading-none max-w-full"
                            data-testid="cover-names"
                            translate="no"
                        >
                            {coupleNames}
                        </h1>
                        <button
                            type="button"
                            onClick={onOpen}
                            data-testid="cover-open-btn"
                            className="wd-delay-5 wd-fade-up wd-btn wd-btn-gold mt-6"
                        >
                            {t("clickToOpen")}
                            <ChevronDown size={16} />
                        </button>
                    </div>
                </div>
            </div>

            <button
                onClick={onOpen}
                className="mt-6 text-[var(--wd-accent)] wd-bounce-down"
                aria-label="Open invitation"
            >
                <ChevronDown size={24} />
            </button>
        </section>
    );
}
